"use client";

import Link from "next/link";
import { Facebook, Instagram, Youtube } from "lucide-react";
import CtaLink from "@/shared/components/common/CtaLink";

export default function LandingFooter() {
  return (
    <footer className="bg-white border-t border-[#eee]">
      <div className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        {/* Top section: Logo + links */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-10">
          {/* Brand */}
          <div className="sm:max-w-xs">
            <img src="/logo.png" alt="Hoku" width={256} height={263} className="h-12 w-auto mb-4" loading="lazy" />
            <p className="text-[#6b6b6b] text-[13px] sm:text-[14px] leading-relaxed font-patrick">
              Your AI-powered wardrobe assistant. Upload once, get styled every
              day.
            </p>
          </div>

          {/* Links */}
          <div className="grid grid-cols-2 gap-x-16 gap-y-8 sm:gap-x-20">
            {/* Product */}
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#767676] font-winky mb-4">
                Product
              </p>
              <div className="space-y-3">
                <CtaLink
                  ctaName="login"
                  ctaLocation="footer"
                  href="/login"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  Login
                </CtaLink>
                <CtaLink
                  ctaName="try_demo"
                  ctaLocation="footer"
                  href="/demo"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  Try Demo
                </CtaLink>
                <Link
                  href="/pricing"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  Pricing
                </Link>
                {/* The Journal has its own root layout, so this is a full page
                    load rather than a client transition. */}
                <Link
                  href="/blog"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  Blog
                </Link>
                <Link
                  href="/about"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  About
                </Link>
              </div>
            </div>

            {/* Legal */}
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#767676] font-winky mb-4">
                Legal
              </p>
              <div className="space-y-3">
                <Link
                  href="/legal/privacy"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  Privacy
                </Link>
                <Link
                  href="/legal/terms"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  Terms
                </Link>
                <Link
                  href="/support"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  Support
                </Link>
                <Link
                  href="/legal/cookies"
                  className="block text-[14px] text-[#2d2d2d] font-winky hover:text-[#6b6b6b] transition-colors"
                >
                  Cookies
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-[#eee] mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Socials */}
          <div className="flex items-center gap-5">
            <a
              href="https://www.instagram.com/heyhoku_app?igsh=MTk1N2ljYmVvbm5sNw=="
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-[#767676] hover:text-[#E1306C] transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://www.facebook.com/share/1B2MFe22nc/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="text-[#767676] hover:text-[#1877F2] transition-colors"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="https://x.com/HeyHoku_ai?t=_88Jw-4-n5v5Ne9NiaNBbg&s=09"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X"
              className="text-[#767676] hover:text-black transition-colors"
            >
              <img src="/footer/twitter.png" alt="X" width={20} height={20} className="w-5 h-5 opacity-50 hover:opacity-100 transition-opacity" loading="lazy" />
            </a>
            <a
              href="https://youtube.com/@heyhokuapp?si=32Lb7gPdHXUj0CfD"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="text-[#767676] hover:text-[#FF0000] transition-colors"
            >
              <Youtube className="w-5 h-5" />
            </a>
            <a
              href="https://www.reddit.com/u/heyhoku/s/kNim3c8Ozj"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Reddit"
              className="text-[#767676] hover:text-[#FF4500] transition-colors"
            >
              <img src="/footer/reddit.png" alt="Reddit" width={20} height={20} className="w-5 h-5 opacity-50 hover:opacity-100 transition-opacity" loading="lazy" />
            </a>
          </div>

          <p className="text-[#767676] text-[12px] font-winky">
            &copy; {new Date().getFullYear()} Hey Hoku. All rights reserved.
          </p>
        </div>

        {/* Mitgo verification token removed (SEO-023). If Admitad re-requires
            verification, use a <meta> tag in <head> instead of visible text. */}
      </div>
    </footer>
  );
}
